import { useField, useFormikContext } from 'formik';
import { rgba } from 'polished';
import { type ComponentProps, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { withFormik } from '../../common/Formik';
import { tokens } from '../../theme/tokens';

const OtpInputWrapper = styled.div`
	position: relative;
`;

const HiddenInput = styled.input`
	position: absolute;
	top: 0;
	left: 0;
	width: 0;
	height: 0;
	opacity: 0;
	pointer-events: none;
`;

const OtpInputContainer = styled.div`
	display: flex;
	gap: 8px;
	width: 100%;
	justify-content: center;
`;

const OtpInputCell = styled.div<{ $filled?: boolean; $cursor?: boolean }>`
	width: 40px;
	height: 50px;
	border-style: solid;
	border-width: 1px;
	border-color: ${(props) =>
		props.$filled ? props.theme.colors.lightBlueDark : tokens.border.subtle};
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${(props) => (!props.$filled ? tokens.text.secondary : 'unset')};
	font-size: 24px;
	user-select: ${(props) => (props.$filled ? 'text' : 'none')};
	cursor: text;

	transition:
		border-color 0.1s,
		border-width 0.1s,
		color 0.1s;

	${(props) =>
		props.$cursor &&
		css`
			box-shadow:
				0px 0px 0px 1px ${(props) => props.theme.colors.primary},
				0px 0px 0px 3px ${(props) => rgba(props.theme.colors.primary, 0.25)},
				0px 1px 2px ${(props) => props.theme.colors.lightBlueDark} inset;
			border-width: 0px;
		`}
`;

type OtpInputProps = Omit<
	ComponentProps<typeof HiddenInput>,
	'onBlur' | 'onFocus' | 'onKeyDown'
> & {
	length: number;
	onComplete?: () => void;
};

export function OtpInput(props: OtpInputProps) {
	const inputRef = useRef<HTMLInputElement>(null);

	const [isFocused, setIsFocused] = useState(false);

	const onFocus = () => {
		if (props.value.length === props.length) {
			props.onChange?.('');
		}

		setIsFocused(true);
	};

	return (
		<OtpInputWrapper>
			<HiddenInput
				{...props}
				type="tel"
				autoComplete="one-time-code"
				onChange={(event) => {
					const onlyNumbers = event.currentTarget.value.replace(/\D/g, '');
					const maxLength = onlyNumbers.substring(0, props.length);

					props.onChange?.(maxLength);

					if (maxLength.length === props.length) {
						props.onComplete?.();
					}
				}}
				onKeyDown={(event) => {
					props.onKeyDown?.(event);
					inputRef.current?.setSelectionRange(props.value.length, props.value.length);
				}}
				onFocus={onFocus}
				onBlur={() => setIsFocused(false)}
				ref={inputRef}
			/>
			<OtpInputContainer>
				{Array.from({ length: props.length }).map((_, index) => (
					<OtpInputCell
						key={`${index}`}
						onClick={() => inputRef.current?.focus()}
						$filled={index < props.value.length}
						$cursor={isFocused && index === Math.min(props.value.length, props.length - 1)}
					>
						{index < props.value.length ? props.value?.[index] : '-'}
					</OtpInputCell>
				))}
			</OtpInputContainer>
		</OtpInputWrapper>
	);
}

const FormikElement = withFormik(OtpInput);

OtpInput.Formik = function OtpInputFormik(
	props: Omit<ComponentProps<typeof HiddenInput>, 'value' | 'onChange'> & {
		validate?: boolean;
		name: string;
	},
) {
	const form = useFormikContext();
	const [field, meta] = useField(props.name);

	return (
		<FormikElement
			{...props}
			onKeyDown={(event) => {
				if (event.key === 'Enter') {
					if (field.value.length === props.length) {
						form.submitForm();
					}

					event.preventDefault();
					return;
				}
			}}
			error={props.error && meta.error}
			valid={props.validate && !meta.error && meta.touched}
		/>
	);
};
